import { useRef, useState, type ChangeEvent } from "react";
import { Plus, RefreshCw, Star, Trash2 } from "lucide-react";
import { Icon, Sheet } from "@cntrl/ui";
import { errorMessage } from "../../app/hooks/apiError.js";
import { pushToast } from "../../app/toasts.js";
import { useMediaObjectUrl, useUploadMedia } from "../../app/hooks/useMedia.js";
import type { PhotoDraft } from "./photoSwap.js";
import styles from "./MobilePhotoTiles.module.scss";

export interface PhotoSlotDef {
  slot: number;
  name: string;
}

export interface MobilePhotoTilesProps {
  slots: readonly PhotoSlotDef[];
  drafts: PhotoDraft[];
  /** The slot whose placement makes it the Dashboard hero — it wears the HERO tag. */
  heroSlot: number | null;
  onUploaded: (slot: number, mediaId: string, url: string | null, contentType: string | null) => void;
  onMakeHero: (slot: number) => void;
  onRemove: (slot: number) => void;
}

/**
 * Photos on the PHONE — a different component from the desktop rows (CD mobile Assets), not a
 * reflow of them: a strip of image tiles (the first wider, 110px tall) with the slot name over the
 * shot, a HERO tag on the Dashboard-hero slot, and dashed `+ ADD` tiles for empty slots.
 *
 * Two deliberate departures from the CD mobile screen, both following rulings already taken:
 * - **All five slots** (the CD phone screen edits three) — so a phone-only user can still fix an Alt
 *   photo. The strip SCROLLS horizontally inside the card to fit them; the page itself never does.
 * - **The HERO tag follows the PLACEMENT** (our ratified desktop hero model — Dashboard Page,
 *   defaulting to the Live slot), not a hardcoded photo1 as the CD phone screen does. Same class of
 *   fix as the logo White/Black/Color vocabulary: one model, one vocabulary, both platforms.
 *
 * Tapping a tile opens the action sheet (Replace · Make hero · Remove); an empty tile picks a file
 * straight away. The CD tap-to-VIEW full-screen viewer (with long-press for the sheet) is deferred —
 * it needs a viewer component; tap-for-actions is the honest interim and the hint line says so.
 */
export function MobilePhotoTiles({
  slots, drafts, heroSlot, onUploaded, onMakeHero, onRemove,
}: MobilePhotoTilesProps) {
  const { uploadMedia } = useUploadMedia();
  const [sheetSlot, setSheetSlot] = useState<number | null>(null);
  const [busySlot, setBusySlot] = useState<number | null>(null);
  // ONE hidden input retargeted per tile via the ref (the ProductionScreen precedent).
  const fileInput = useRef<HTMLInputElement>(null);
  const pendingSlot = useRef<number>(1);

  const pickFor = (slot: number) => {
    pendingSlot.current = slot;
    fileInput.current?.click();
  };

  const onPick = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const slot = pendingSlot.current;
    setBusySlot(slot);
    try {
      const asset = await uploadMedia(file, { kind: "image", purpose: "band-photo" });
      if (asset.mediaId) onUploaded(slot, asset.mediaId, asset.url ?? null, asset.contentType ?? null);
    } catch (err) {
      pushToast(errorMessage(err, "Couldn't upload the photo."), "error");
    } finally {
      setBusySlot(null);
    }
  };

  const sheetDef = slots.find((s) => s.slot === sheetSlot);

  return (
    <>
      <div className={styles.strip} data-hscroll="photos">
        {slots.map((def) => {
          const draft = drafts.find((d) => d.slot === def.slot) ?? null;
          return (
            <PhotoTile
              key={def.slot}
              def={def}
              draft={draft}
              isHero={heroSlot === def.slot}
              busy={busySlot === def.slot}
              onTap={() => (draft ? setSheetSlot(def.slot) : pickFor(def.slot))}
            />
          );
        })}
      </div>
      <p className={styles.hint}>Tap a photo for options</p>
      <input ref={fileInput} type="file" accept="image/*" className={styles.hiddenInput} onChange={onPick} />

      {sheetSlot !== null && sheetDef && (
        <Sheet title={sheetDef.name} onClose={() => setSheetSlot(null)} closeLabel="Close photo options">
          <ul className={styles.sheetList}>
            <li>
              <button type="button" className={styles.sheetRow}
                onClick={() => { const s = sheetSlot; setSheetSlot(null); pickFor(s); }}>
                <Icon icon={RefreshCw} size={20} />
                <span>Replace</span>
              </button>
            </li>
            {heroSlot !== sheetSlot && (
              <li>
                <button type="button" className={styles.sheetRow}
                  onClick={() => { onMakeHero(sheetSlot); setSheetSlot(null); }}>
                  <Icon icon={Star} size={20} />
                  <span>Make hero</span>
                </button>
              </li>
            )}
            <li>
              <button type="button" className={`${styles.sheetRow} ${styles.sheetRowDanger}`}
                onClick={() => { onRemove(sheetSlot); setSheetSlot(null); }}>
                <Icon icon={Trash2} size={20} />
                <span>Remove</span>
              </button>
            </li>
          </ul>
        </Sheet>
      )}
    </>
  );
}

function PhotoTile({ def, draft, isHero, busy, onTap }: {
  def: PhotoSlotDef; draft: PhotoDraft | null; isHero: boolean; busy: boolean; onTap: () => void;
}) {
  const resolved = useMediaObjectUrl(draft?.mediaId ?? null, draft?.thumbUrl ?? draft?.url ?? null, { variant: "thumb" });
  const empty = draft === null;

  return (
    <button
      type="button"
      className={`${styles.tile} ${empty ? styles.tileEmpty : ""} ${def.slot === 1 ? styles.tileWide : ""}`}
      style={!empty && resolved.url ? { backgroundImage: `url(${resolved.url})` } : undefined}
      aria-label={empty ? `Add ${def.name}` : `${def.name} options`}
      onClick={onTap}
      disabled={busy}
    >
      {empty ? (
        <>
          <Icon icon={Plus} size={20} />
          <span className={styles.addLabel}>Add</span>
        </>
      ) : (
        <>
          {isHero && <span className={styles.heroTag}>Hero</span>}
          <span className={styles.tileLabel}>{busy ? "Uploading…" : def.name}</span>
        </>
      )}
    </button>
  );
}
